import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  MouseSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import React from "react";

import { useNotification } from "@/shared/components/notifications/useNotification";
import { FSType } from "@/shared/domain/files/FileRecord";
import {
  DirectoryNode,
  FileSystemNode,
  isDirectory,
  isFile,
} from "@/shared/domain/files/filesystem";
import {
  useMoveDirectoryMutation,
  useMoveFileMutation,
} from "@/shared/domain/files/hooks";
import { useBulkMoveNodesMutation } from "@/shared/domain/files/hooks/useBulkMoveNodesMutation";
import { MoveNodeOperation } from "@/shared/domain/files/operations";
import { RobotoDomainException } from "@/shared/http";

import { FileBrowserAction, FileBrowserActionTypes } from "./actions";
import { FileDragOverlay } from "./FileDragOverlay";
import { isPathWithinSubtree, summarizeMoveSettlement } from "./helper";

/** Droppable IDs for breadcrumb crumbs use this prefix to distinguish them from folder-row targets. */
export const CRUMB_DROP_PREFIX = "crumb-drop:";

/** Move failures linger longer than the default 3s so the user can read why the move was rejected. */
const ERROR_NOTIFICATION_DURATION_MS = 6000;

interface FileBrowserDndContextProps {
  children: React.ReactNode;
  datasetId: string;
  dispatch: React.Dispatch<FileBrowserAction>;
  filePath: string;
  selectedItems: FileSystemNode[];
  onValidDrop?: (event: DragEndEvent, fs_nodes: FileSystemNode[]) => void;
}

export const FileBrowserDndContext: React.FC<FileBrowserDndContextProps> = ({
  children,
  datasetId,
  dispatch,
  filePath,
  selectedItems,
  onValidDrop,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(MouseSensor, {
      activationConstraint: { distance: 4 },
    }),
  );

  const [activeDraggable, setActiveDraggable] =
    React.useState<FileSystemNode | null>(null);
  const [shouldAnimateDrop, setShouldAnimateDrop] = React.useState(true);
  const showNotification = useNotification();

  const bulkMoveNodesMutation = useBulkMoveNodesMutation();
  const moveFileMutation = useMoveFileMutation();
  const moveDirectoryMutation = useMoveDirectoryMutation();

  const handleDragStart = (event: DragStartEvent) => {
    const activeData = event.active.data.current?.data as FileSystemNode;
    setActiveDraggable(activeData);

    if (!isActiveDraggableInSelectedItems(activeData, selectedItems)) {
      dispatch({
        type: FileBrowserActionTypes.ClearSelectedItems,
        payload: null,
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDraggable(null);

    if (!event.over) {
      setShouldAnimateDrop(true);
      return;
    }

    if (event.active.id === event.over.id) {
      setShouldAnimateDrop(true);
      return;
    }

    const overId = String(event.over.id);

    // ── Drop on a breadcrumb crumb ──────────────────────────────────────────
    if (overId.startsWith(CRUMB_DROP_PREFIX)) {
      const destinationPath = overId.slice(CRUMB_DROP_PREFIX.length);
      const eventData = event.active.data.current?.data as FileSystemNode;
      const nodesToMove = isActiveDraggableInSelectedItems(
        eventData,
        selectedItems,
      )
        ? selectedItems
        : [eventData];

      if (destinationPath === filePath) {
        setShouldAnimateDrop(true);
        return;
      }

      // Prevent moving a directory into its own subtree
      for (const node of nodesToMove) {
        if (isDirectory(node)) {
          if (
            isPathWithinSubtree(destinationPath, node.directory.relative_path)
          ) {
            setShouldAnimateDrop(true);
            return;
          }
        }
      }

      setShouldAnimateDrop(false);
      onValidDrop?.(event, nodesToMove);

      const moveAttempts = nodesToMove.map((node) => {
        const newPath =
          destinationPath === ""
            ? node.name
            : `${destinationPath}/${node.name}`;

        let promise: Promise<unknown> = Promise.resolve();
        if (isFile(node)) {
          promise = moveFileMutation.mutateAsync({
            fileId: node.file.file_id,
            datasetId,
            currentPath: node.file.relative_path,
            newFullPath: newPath,
          });
        } else if (isDirectory(node)) {
          promise = moveDirectoryMutation.mutateAsync({
            datasetId,
            currentPath: node.directory.relative_path,
            newFullPath: newPath,
          });
        }
        return { node, promise };
      });

      void Promise.allSettled(
        moveAttempts.map((attempt) => attempt.promise),
      ).then((results) => {
        const summary = summarizeMoveSettlement(results);

        switch (summary.outcome) {
          case "all-succeeded": {
            dispatch({
              type: FileBrowserActionTypes.ClearSelectedItems,
              payload: null,
            });
            showNotification("Move complete", "success");
            return;
          }
          case "all-failed": {
            const { error } = summary;
            const message =
              error instanceof RobotoDomainException && error.isClientError()
                ? error.message
                : "Unable to move items. Please try again.";
            showNotification(message, "error", ERROR_NOTIFICATION_DURATION_MS);
            return;
          }
          case "partial": {
            // Keep only the failed items selected so the user can retry them.
            const failedNodes = moveAttempts
              .filter((_, index) => results[index].status === "rejected")
              .map((attempt) => attempt.node);
            dispatch({
              type: FileBrowserActionTypes.SetSelectedItems,
              payload: failedNodes,
            });
            showNotification(
              `${summary.failedCount} of ${summary.total} items failed to move`,
              "warning",
              ERROR_NOTIFICATION_DURATION_MS,
            );
            return;
          }
        }
      });

      return;
    }

    // ── Drop on a folder row (existing "move into" behavior) ───────────────
    const eventData = event.active.data.current?.data as FileSystemNode;
    const hoverData = event.over.data.current?.data as DirectoryNode;

    const shouldUseSelectedItems = isActiveDraggableInSelectedItems(
      eventData,
      selectedItems,
    );

    if (
      isDropZoneInSelectedItems(hoverData.directory.directory_id, selectedItems)
    ) {
      setShouldAnimateDrop(true);
      return;
    }

    const nodesToMove: FileSystemNode[] = shouldUseSelectedItems
      ? selectedItems
      : [eventData];

    onValidDrop?.(event, nodesToMove);
    setShouldAnimateDrop(false);

    const operations: MoveNodeOperation[] = [];
    for (const node of nodesToMove) {
      if (isFile(node)) {
        operations.push({
          node_id: node.file.file_id,
          fs_type: FSType.File,
          new_parent_id: hoverData.directory.directory_id,
          new_name: node.name,
        });
      } else if (isDirectory(node)) {
        operations.push({
          node_id: node.directory.directory_id,
          fs_type: FSType.Directory,
          new_parent_id: hoverData.directory.directory_id,
          new_name: node.name,
        });
      }
    }

    bulkMoveNodesMutation
      .mutateAsync({
        datasetId: hoverData.directory.association_id,
        operations,
        nodes: nodesToMove,
        sourcePath: filePath,
        destinationPath: hoverData.directory.relative_path,
      })
      .then(() => {
        dispatch({
          type: FileBrowserActionTypes.ClearSelectedItems,
          payload: null,
        });
        showNotification("Move complete", "success");
      })
      .catch((error: unknown) => {
        const message =
          error instanceof RobotoDomainException && error.isClientError()
            ? error.message
            : "Unable to move items. Please try again.";
        showNotification(message, "error", ERROR_NOTIFICATION_DURATION_MS);
      });
  };

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sensors={sensors}
      collisionDetection={pointerWithin}
    >
      {children}

      <DragOverlay dropAnimation={shouldAnimateDrop ? dropAnimation : null}>
        {activeDraggable?.name && (
          <FileDragOverlay
            activeDraggable={activeDraggable}
            itemCount={selectedItems.length}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

const dropAnimation: DropAnimation = {
  duration: 300,
  easing: "ease-out",
  keyframes({
    transform,
  }: {
    transform: { initial: { x: number; y: number } };
  }) {
    const correctedStart = `translate(${transform.initial.x}px, ${transform.initial.y}px) `;
    const correctedEnd = `translate(0, 0)`;
    return [
      { transform: correctedStart, opacity: 1 },
      { transform: correctedEnd, opacity: 1 },
    ];
  },
  sideEffects: null,
};

const isActiveDraggableInSelectedItems = (
  activeDraggable: FileSystemNode,
  selectedItems: FileSystemNode[],
) => {
  return selectedItems.find((item) => {
    if (isFile(item)) {
      if (isFile(activeDraggable)) {
        return item.file.file_id === activeDraggable.file.file_id;
      }
      return false;
    } else if (isDirectory(item)) {
      if (isDirectory(activeDraggable)) {
        return (
          item.directory.relative_path ===
          activeDraggable.directory.relative_path
        );
      }
      return false;
    }
    return false;
  });
};

const isDropZoneInSelectedItems = (
  dropZoneId: string,
  selectedItems: FileSystemNode[],
) => {
  return selectedItems.find((item) => {
    if (isDirectory(item)) {
      return item.directory.directory_id === dropZoneId;
    }
    return false;
  });
};
